import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, TrackByFunction } from '@angular/core';
import { PropertyGroup } from '../floating-inspector/property-grouper.service';

export interface VirtualPropertyItem {
  id: string;
  type: 'group-header' | 'property' | 'separator';
  data: any;
  height: number;
  groupId?: string;
}

@Component({
  selector: 'app-virtual-scroll-property-list',
  template: `
    <cdk-virtual-scroll-viewport 
      itemSize="60" 
      class="virtual-property-viewport"
      [style.height.px]="viewportHeight">
      
      <div 
        *cdkVirtualFor="let item of virtualItems; 
                       trackBy: trackByItem; 
                       templateCacheSize: 20"
        class="virtual-property-item"
        [style.height.px]="item.height"
        [attr.data-item-type]="item.type"
        [attr.data-group-id]="item.groupId">
        
        <!-- Group Header -->
        <div *ngIf="item.type === 'group-header'" class="group-header-virtual">
          <nz-collapse-panel 
            [nzHeader]="item.data.title"
            [nzActive]="!isGroupCollapsed(item.data.id)"
            (nzActiveChange)="onGroupToggle(item.data.id)">
            
            <ng-template #nzCollapseHeader>
              <div class="group-header-content">
                <nz-icon [nzType]="item.data.icon || 'setting'" nzTheme="outline"></nz-icon>
                <span class="group-title">{{ item.data.title }}</span>
                <nz-badge 
                  [nzCount]="item.data.properties.length" 
                  [nzShowZero]="false" 
                  nzSize="small">
                </nz-badge>
              </div>
            </ng-template>
          </nz-collapse-panel>
        </div>

        <!-- Property Item -->
        <div *ngIf="item.type === 'property'" class="property-item-virtual">
          <app-property-control
            [property]="item.data.property"
            [component]="currentComponent"
            [displayName]="item.data.displayName"
            [description]="item.data.description"
            [compact]="compactMode">
          </app-property-control>
        </div>

        <!-- Separator -->
        <div *ngIf="item.type === 'separator'" class="separator-virtual">
          <nz-divider></nz-divider>
        </div>
      </div>
    </cdk-virtual-scroll-viewport>
  `,
  styleUrls: ['./virtual-scroll-property-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class VirtualScrollPropertyListComponent {
  @Input() propertyGroups: PropertyGroup[] = [];
  @Input() currentComponent: any = null;
  @Input() compactMode = false;
  @Input() viewportHeight = 400;
  @Input() collapsedGroups = new Set<string>();
  
  @Output() groupToggle = new EventEmitter<string>();
  @Output() propertyChange = new EventEmitter<{ property: string; value: any }>();

  get virtualItems(): VirtualPropertyItem[] {
    const items: VirtualPropertyItem[] = [];
    
    this.propertyGroups.forEach((group, groupIndex) => {
      // Add group header
      items.push({
        id: `group-${group.id}`,
        type: 'group-header',
        data: group,
        height: 48,
        groupId: group.id,
      });

      // Add properties if group is expanded
      if (!this.isGroupCollapsed(group.id)) {
        group.properties.forEach((property, propIndex) => {
          items.push({
            id: `prop-${group.id}-${property}`,
            type: 'property',
            data: {
              property,
              displayName: this.getPropertyDisplayName(property),
              description: this.getPropertyDescription(property),
            },
            height: this.compactMode ? 48 : 64,
            groupId: group.id,
          });
        });

        // Add separator between groups (except last)
        if (groupIndex < this.propertyGroups.length - 1) {
          items.push({
            id: `sep-${group.id}`,
            type: 'separator',
            data: null,
            height: 16,
            groupId: group.id,
          });
        }
      }
    });

    return items;
  }

  trackByItem: TrackByFunction<VirtualPropertyItem> = (index: number, item: VirtualPropertyItem) => {
    return item.id;
  };

  isGroupCollapsed(groupId: string): boolean {
    return this.collapsedGroups.has(groupId);
  }

  onGroupToggle(groupId: string) {
    this.groupToggle.emit(groupId);
  }

  private getPropertyDisplayName(property: string): string {
    // Convert camelCase to readable format
    return property
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  private getPropertyDescription(property: string): string {
    // Provide contextual descriptions for common properties
    const descriptions: Record<string, string> = {
      backgroundColor: 'Set the background color',
      textColor: 'Set the text color',
      fontSize: 'Set the font size',
      padding: 'Set the padding',
      margin: 'Set the margin',
      borderRadius: 'Set the border radius',
      required: 'Make this field required',
      placeholder: 'Set placeholder text',
      label: 'Set the field label',
    };
    
    return descriptions[property] || `Configure ${this.getPropertyDisplayName(property).toLowerCase()}`;
  }
}
